import type { Ref } from 'vue';

/**
 * Time Input Validation Utilities
 * Handles validation and clamping of time input values
 */

/**
 * Clamp a value between min and max
 * @param value - Value to clamp
 * @param min - Minimum allowed value
 * @param max - Maximum allowed value
 * @returns Clamped value
 */
export const clampValue = (value: number, min: number, max: number): number => {
    return value < min ? min : value > max ? max : value;
};

/**
 * Restrict input length to 2 digits
 * @param target - HTML input element
 * @returns Restricted value string
 */
export const restrictInputLength = (target: HTMLInputElement): string => {
    let value = target.value;
    if (value.length > 2) {
        value = value.slice(0, 2);
        target.value = value;
    }
    return value;
};

/**
 * Validate and clamp hours value (1-12 for 12-hour format)
 * @param hoursRef - Ref containing hours value
 * @param onChange - Callback to trigger after validation
 */
export const validateHours = (
    hoursRef: Ref<number | null>,
    onChange: () => void
): void => {
    if (hoursRef.value === null) return;

    const clamped = clampValue(hoursRef.value, 1, 12);
    if (hoursRef.value !== clamped) {
        hoursRef.value = clamped;
        onChange();
    }
};

/**
 * Validate and clamp minutes value (0-59)
 * @param minutesRef - Ref containing minutes value
 * @param onChange - Callback to trigger after validation
 */
export const validateMinutes = (
    minutesRef: Ref<number | null>,
    onChange: () => void
): void => {
    if (minutesRef.value === null) return;

    const clamped = clampValue(minutesRef.value, 0, 59);
    if (minutesRef.value !== clamped) {
        minutesRef.value = clamped;
        onChange();
    }
};

/**
 * Auto-advance focus to minutes input
 */
const focusMinutesInput = (minutesInputRef: Ref<HTMLInputElement | null>): void => {
    setTimeout(() => {
        if (minutesInputRef.value) {
            minutesInputRef.value.focus();
        }
    }, 0);
};

/**
 * Move digit to minutes field and focus
 */
const moveDigitToMinutes = (
    digit: number,
    minutesRef: Ref<number | null>,
    minutesInputRef: Ref<HTMLInputElement | null>
): void => {
    minutesRef.value = digit;
    setTimeout(() => {
        if (minutesInputRef.value) {
            minutesInputRef.value.value = digit.toString();
            minutesInputRef.value.focus();
            minutesInputRef.value.setSelectionRange(1, 1);
        }
    }, 0);
};

interface HourInputContext {
    hoursRef: Ref<number | null>;
    minutesRef: Ref<number | null>;
    minutesInputRef: Ref<HTMLInputElement | null>;
    target: HTMLInputElement;
    onChange: () => void;
}

/**
 * Handle single digit hour input (2-9)
 */
const handleSingleDigitHour = (
    numValue: number,
    context: HourInputContext
): boolean => {
    if (numValue >= 2 && numValue <= 9) {
        context.hoursRef.value = numValue;
        context.target.value = numValue.toString();
        context.onChange();
        focusMinutesInput(context.minutesInputRef);
        return true;
    }
    return false;
};

/**
 * Handle two digit hour input (10-12, 13-19)
 */
const handleTwoDigitHour = (
    value: string,
    context: HourInputContext
): boolean => {
    const firstDigit = parseInt(value[0], 10);
    const secondDigit = parseInt(value[1], 10);
    const twoDigitValue = parseInt(value.substring(0, 2), 10);

    // Valid values: 10, 11, 12
    if (twoDigitValue >= 10 && twoDigitValue <= 12) {
        context.hoursRef.value = twoDigitValue;
        context.target.value = twoDigitValue.toString();
        context.onChange();
        focusMinutesInput(context.minutesInputRef);
        return true;
    }

    // Invalid values 13-19: keep first digit, move second to minutes
    if (firstDigit === 1 && twoDigitValue > 12) {
        context.hoursRef.value = firstDigit;
        context.target.value = firstDigit.toString();
        moveDigitToMinutes(secondDigit, context.minutesRef, context.minutesInputRef);
        context.onChange();
        return true;
    }

    // Values starting with 2-9: keep first digit, move second to minutes
    if (firstDigit >= 2) {
        context.hoursRef.value = firstDigit;
        context.target.value = firstDigit.toString();
        moveDigitToMinutes(secondDigit, context.minutesRef, context.minutesInputRef);
        context.onChange();
        return true;
    }

    return false;
};

interface HoursInputParams {
    hoursRef: Ref<number | null>;
    minutesRef: Ref<number | null>;
    minutesInputRef: Ref<HTMLInputElement | null>;
    onChange: () => void;
}

/**
 * Handle smart input for hours with auto-advance to minutes
 * @param event - Input event
 * @param params - Object containing refs and callback
 */
export const handleHoursInput = (
    event: Event,
    params: HoursInputParams
): void => {
    const target = event.target as HTMLInputElement;
    let value = target.value.replace(/\D/g, ''); // Remove non-digits

    if (!value) {
        params.hoursRef.value = null;
        target.value = '';
        params.onChange();
        return;
    }

    const numValue = parseInt(value, 10);
    const context: HourInputContext = {
        hoursRef: params.hoursRef,
        minutesRef: params.minutesRef,
        minutesInputRef: params.minutesInputRef,
        target,
        onChange: params.onChange
    };

    // Handle single digit (2-9)
    if (value.length === 1) {
        const handled = handleSingleDigitHour(numValue, context);
        if (handled) return;
        
        // For single digit "1", just set the value but don't trigger onChange
        // User is likely typing "10", "11", or "12"
        if (numValue === 1) {
            params.hoursRef.value = 1;
            target.value = '1';
            return; // Don't call onChange yet
        }
    }

    // Handle two or more digits
    if (value.length >= 2) {
        const handled = handleTwoDigitHour(value, context);
        if (handled) return;
        value = value.substring(0, 2);
    }

    // Default: set value and wait for next input
    params.hoursRef.value = parseInt(value, 10);
    target.value = value;
    params.onChange();
};

/**
 * Handle input event for time fields (minutes)
 * Restricts length to 2 digits and converts empty string to null
 * Preserves "00" display for minutes
 * @param field - Field name
 * @param event - Input event
 * @param fieldMap - Map of field names to refs
 * @param onChange - Callback to trigger after input
 */
export const handleTimeInput = (
    field: string,
    event: Event,
    fieldMap: Record<string, Ref<number | null>>,
    onChange: () => void
): void => {
    const target = event.target as HTMLInputElement;
    let value = target.value;
    
    // Remove any non-digit characters
    value = value.replace(/\D/g, '');
    
    // Restrict to 2 digits
    if (value.length > 2) {
        value = value.slice(0, 2);
    }
    
    const fieldRef = fieldMap[field];
    if (!fieldRef) return;

    if (value === '') {
        fieldRef.value = null;
        target.value = '';
    } else {
        const numValue = parseInt(value, 10);
        fieldRef.value = numValue;
        
        // For display: preserve leading zero for minutes (e.g., "00", "01", "09")
        // Only preserve if user typed 2 digits or if it's a leading zero
        if (field.includes('Minutes') && value.length === 2) {
            target.value = value; // Keep "00", "01", "09" etc.
        } else {
            target.value = numValue.toString(); // Normal display
        }
    }
    
    onChange();
};
